import type {CmLogger} from "@cm/log"
import {Catalog} from "@cm/pricing/catalogs/catalog-interface"
import {PriceGraphData} from "@cm/pricing/catalogs/graph-helpers"
import {CalypsoCatalog, parseXlsx as parseXlsxCalypso} from "@cm/pricing/catalogs/calypso/catalog"
import {
    createBrentaGraph,
    createDiamanteGraph,
    createLevantoGraph,
    createPetraGraph,
    createTesimaGraph,
    createUltimoGraph,
    createValorGraph,
} from "@cm/pricing/catalogs/calypso/price-graphs"
import {parseXlsb as parseXlsbVado, VadoCatalog} from "@cm/pricing/catalogs/vado/catalog"
import {createSafariPriceGraphV2} from "@cm/pricing/catalogs/vado/safari-graph-v2"
import {createCameoPriceGraphV2} from "@cm/pricing/catalogs/vado/cameo-graph-v2"
import {DelvauxCatalog, parseXlsx as parseXlsxDelvaux} from "@cm/pricing/catalogs/delvaux/catalog"
import {createMtoPriceGraph} from "@cm/pricing/catalogs/delvaux/bag-graph"

type Xlsx = typeof import("xlsx")

/*Customer catalogs are messy and change format between deliveries, so the parser is fixed per catalog here instead of being guessed from the file.
A customer can have multiple catalogs (e.g. one per product line), each template uses exactly one.*/
export type CatalogDef<C extends Catalog = Catalog> = {
    id: string
    customer: string
    asset: string //path relative to the web-app root, files are local only (not checked in)
    parse: (data: Uint8Array, xlsx: Xlsx, logger: CmLogger) => C
}

export type TemplateEntry = {
    label: string
    templateIds: string[]
    catalog: CatalogDef
    createGraph: (catalog: Catalog) => PriceGraphData
}

//Ties the catalog type to the graph initializer, the cast below is safe because the catalog is always created via catalog.parse.
function entry<C extends Catalog>(label: string, templateIds: string[], catalog: CatalogDef<C>, createGraph: (catalog: C) => PriceGraphData): TemplateEntry {
    return {label, templateIds, catalog, createGraph: (c) => createGraph(c as C)}
}

const calypso: CatalogDef<CalypsoCatalog> = {
    id: "calypso",
    customer: "Calypso",
    asset: "assets/00 - Calypso-Price-File-Spring-26-V3 - on prod.xlsx",
    parse: (data, xlsx, logger) => parseXlsxCalypso(data, xlsx, logger) as CalypsoCatalog,
}

const vadoSafari: CatalogDef<VadoCatalog> = {
    id: "vado-safari",
    customer: "Vado",
    asset: "assets/VADO Colormass Price Lists - October 2026 - Final 05.08.26.xlsx",
    parse: (data, xlsx, logger) => parseXlsbVado(data, xlsx, logger) as VadoCatalog,
}

const vadoCameoOld: CatalogDef<VadoCatalog> = {
    id: "vado-cameo-old",
    customer: "Vado",
    asset: "assets/2025-09-16-Cameo price list, joined with additions - on prod.xlsx",
    parse: (data, xlsx, logger) => parseXlsbVado(data, xlsx, logger) as VadoCatalog,
}

const vadoCameoNew: CatalogDef<VadoCatalog> = {
    id: "vado-cameo-new",
    customer: "Vado",
    asset: "assets/2026-08-10 - Cameo Price List.xlsx",
    parse: (data, xlsx, logger) => parseXlsbVado(data, xlsx, logger) as VadoCatalog,
}

const delvauxMto: CatalogDef<DelvauxCatalog> = {
    id: "delvaux-mto",
    customer: "Delvaux",
    asset: "assets/Colormass_FW25 MTO Prijs Lijst_June 2026.xlsx",
    parse: (data, xlsx, logger) => parseXlsxDelvaux(data, xlsx, logger) as DelvauxCatalog,
}

export const TEMPLATE_REGISTRY: TemplateEntry[] = [
    entry("Tesima", ["a3fef545-354c-49f7-a1d4-963e625e8d1f"], calypso, createTesimaGraph),
    entry("Ultimo", ["3b64181f-9686-44b9-88ea-236d1f158779"], calypso, createUltimoGraph),
    entry("Diamante", ["6e17d28a-b3fc-47bd-8a5b-9615a89165a7"], calypso, createDiamanteGraph),
    entry("Brenta", ["7a8e201e-8cd5-4304-b081-ef9f596d449e"], calypso, createBrentaGraph),
    entry("Levanto", ["7ba93af7-e31c-4dac-8df6-42eaa3e29032"], calypso, createLevantoGraph),
    entry("Petra", ["30875a1a-9a72-423b-9f87-bd84ae00bd2a"], calypso, createPetraGraph),
    entry("Valor", ["c99949dc-7888-4e36-beb7-a366fcbfe098"], calypso, createValorGraph),
    entry("Safari", ["93834db4-bd29-43e5-8e18-6b9d41a4453f"], vadoSafari, createSafariPriceGraphV2),
    entry("Cameo (old)", ["d12dfd86-a8fb-46d9-81ab-5491866ce126"], vadoCameoOld, createCameoPriceGraphV2),
    entry("Cameo (new)", ["33dc5c71-1c0e-4e36-94b9-0bfeae9ddd50"], vadoCameoNew, createCameoPriceGraphV2),
    entry("MTO / Bags", ["a92a20c7-434f-41e5-9f70-7e17459f7e27"], delvauxMto, createMtoPriceGraph),
]

export function findTemplateEntry(templateId: string | undefined): TemplateEntry | undefined {
    if (!templateId) return undefined
    return TEMPLATE_REGISTRY.find((entry) => entry.templateIds.includes(templateId))
}

export function groupByCustomer(entries: TemplateEntry[]): {customer: string; entries: TemplateEntry[]}[] {
    const groups = new Map<string, TemplateEntry[]>()
    for (const entry of entries) {
        const group = groups.get(entry.catalog.customer) ?? []
        group.push(entry)
        groups.set(entry.catalog.customer, group)
    }
    return Array.from(groups, ([customer, entries]) => ({customer, entries}))
}
